import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Loading from 'components/Loading';
import MessageBlock from 'components/MessageBlock';
import { getLocale } from 'selectors/user';
import { shouldShowGuestDirectory, isGridView } from 'selectors/features';
import {
  getGuestDirectoryMenus,
  getGuestDirectoryIsLoading,
  getGuestDirectoryError,
} from 'selectors/guestDirectory';
import { MenuContainer } from 'components/Menu/styles';
import { Item, StyledLink, Details, Name, MenuIndicator, IndicatorIcon } from 'components/BrowseLink/styles';
import ChevronForward from 'components/Icons/ChevronForward';
import image from 'utils/image';
import addCssPrefixTo from 'utils/addCssPrefixTo';

const StyledItem = styled(Item)`
  height: 250px;
  min-height: 250px;
`;

const GuestDirectoryMenuSelection: React.FC = () => {
  const currentLocale = useSelector(getLocale);
  const featureEnabled = useSelector(shouldShowGuestDirectory);
  const isGridViewEnabled = useSelector(isGridView);
  const isLoading = useSelector(getGuestDirectoryIsLoading);
  const error = useSelector(getGuestDirectoryError);
  const menus = useSelector(getGuestDirectoryMenus);

  if (!featureEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <Loading>
        <FormattedMessage defaultMessage="Loading Guest Directory..." />
      </Loading>
    );
  }

  if (error) {
    return (
      <MessageBlock
        header={<FormattedMessage defaultMessage="Sorry" />}
        body={<FormattedMessage defaultMessage="Unable to load guest directory. Please try again later." />}
      />
    );
  }

  if (menus.length === 0) {
    return (
      <MessageBlock
        header={<FormattedMessage defaultMessage="Sorry" />}
        body={
          <FormattedMessage defaultMessage="There are currently no guest directory menus available at this time. Please check back soon." />
        }
      />
    );
  }

  const menuHasImages = menus.some(menu => menu.images && menu.images.length > 0);
  const indicatorIconProps = { fill: '#bbbbbb', width: '21px', height: '21px' };

  return (
    <MenuContainer isGridView={isGridViewEnabled}>
      {menus.map(menu => {
        const menuName = menu.translations?.[currentLocale]?.name || menu.name || '';
        const imagePath = menu.images?.[0]?.path;

        return (
          <StyledItem
            key={menu.id}
            hasImage={!!imagePath}
            listView={!menuHasImages}
            filtered={false}
            style={imagePath && { backgroundImage: `url(${image(imagePath, { height: 700 })})` }}
            className={addCssPrefixTo('MENU_LINK')}
          >
            <StyledLink
              image={imagePath ? 1 : 0}
              to={{
                pathname: `/guest-directory/menu/${menu.slug}`,
                state: { transition: 'Forward' },
              }}
            >
              <Details
                hasImage={!!imagePath}
                className={imagePath ? addCssPrefixTo('HAS_IMAGE_DETAILS') : ''}
              >
                <span>
                  <Name>{menuName}</Name>
                </span>
              </Details>
              <MenuIndicator hasImage={!!imagePath}>
                <IndicatorIcon className={imagePath ? addCssPrefixTo('HAS_IMAGE_INDICATOR_ICON') : ''}>
                  <ChevronForward {...indicatorIconProps} />
                </IndicatorIcon>
              </MenuIndicator>
            </StyledLink>
          </StyledItem>
        );
      })}
    </MenuContainer>
  );
};

export default GuestDirectoryMenuSelection;
